import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import SEO from '../SEO';

// Import icons
import { 
  ChartBarIcon, 
  RocketLaunchIcon, 
  LightBulbIcon,
  ArrowRightIcon,
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

export default function AuditResults() {
  const [auditData, setAuditData] = useState(null);
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59
  });
  
  // Refs for animation
  const [resultsRef, resultsInView] = useInView({ 
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [upsellRef, upsellInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Retrieve audit data from sessionStorage
  useEffect(() => {
    const storedData = sessionStorage.getItem('auditData');
    if (storedData) {
      setAuditData(JSON.parse(storedData));
      console.log('Stored audit data:', JSON.parse(sessionStorage.getItem('auditData')));
    }
    
    // Start countdown timer
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  // Format numbers with leading zeros
  const formatNumber = (num) => {
    return num < 10 ? `0${num}` : num;
  };
  
  // Enhanced AI insights parsing based on structured response format
  const getAIInsights = () => {
    if (!auditData) return [];
    
    // If we have AI-generated insights from the Lambda function
    if (auditData.aiInsights) {
      try {
        const aiResponse = auditData.aiInsights;
        const insights = [];
        
        // Helper function to extract sections more consistently
        const extractSection = (sectionName, text) => {
          const regex = new RegExp(`${sectionName}:\\s*(.*?)(?=(AI OPPORTUNITIES:|QUICK WINS:|ROADMAP:|$))`, 'is');
          const match = text.match(regex);
          return match ? match[1].trim() : '';
        };
        
        // Helper function to extract numbered items from a section
        const extractNumberedItems = (sectionText) => {
          // This regex looks for numbered items (1., 2., etc.) and captures everything until the next numbered item or end
          const itemRegex = /(\d+\.\s*)(.*?)(?=(?:\n\s*\d+\.)|$)/gs;
          const items = [];
          let match;
          
          while ((match = itemRegex.exec(sectionText)) !== null) {
            items.push(match[2].trim());
          }
          
          return items;
        };
        
        // Extract sections
        const opportunitiesText = extractSection('AI OPPORTUNITIES', aiResponse);
        const quickWinsText = extractSection('QUICK WINS', aiResponse);
        const roadmapText = extractSection('ROADMAP', aiResponse);
        
        // Process AI OPPORTUNITIES
        const opportunityItems = extractNumberedItems(opportunitiesText);
        opportunityItems.forEach((item, index) => {
          const titleMatch = item.match(/Title:\s*(.*?)(?=\s*Description:|$)/is);
          const descMatch = item.match(/Description:\s*(.*?)(?=\s*Business Impact:|$)/is);
          const impactMatch = item.match(/Business Impact:\s*(.*?)(?=\s*Implementation Complexity:|$)/is);
          const complexityMatch = item.match(/Implementation Complexity:\s*(.*?)(?=$)/is);
          
          // Use null coalescing to provide fallbacks for missing data
          const title = titleMatch?.[1]?.trim() || `AI Opportunity ${index + 1}`;
          const description = descMatch?.[1]?.trim() || '';
          const impact = impactMatch?.[1]?.trim() || '';
          const complexity = complexityMatch?.[1]?.trim() || 'Medium';
          
          // Clean up any leftover formatting or placeholders
          const cleanTitle = title.replace(/\[|\]/g, '').replace(/clear, concise title for the opportunity/i, `AI Opportunity for ${auditData.businessName}`);
          
          insights.push({
            title: cleanTitle,
            description: description,
            impact: impact, // Store impact separately
            complexity: complexity,
            isBasic: index < 2 // First 2 insights are free
          });
        });
        
        // Process QUICK WINS
        const quickWinItems = extractNumberedItems(quickWinsText);
        quickWinItems.forEach((item, index) => {
          const titleMatch = item.match(/Title:\s*(.*?)(?=\s*Description:|$)/is);
          const descMatch = item.match(/Description:\s*(.*?)(?=\s*Business Impact:|$)/is);
          const impactMatch = item.match(/Business Impact:\s*(.*?)(?=$)/is);
          
          const title = titleMatch?.[1]?.trim() || `Quick Win ${index + 1}`;
          const description = descMatch?.[1]?.trim() || '';
          const impact = impactMatch?.[1]?.trim() || '';
          
          // Clean up any leftover formatting or placeholders
          const cleanTitle = title.replace(/\[|\]/g, '').replace(/quick win title/i, `Quick Win for ${auditData.businessName}`);
          
          insights.push({
            title: `Quick Win: ${cleanTitle}`,
            description: description,
            impact: impact, // Store impact separately
            isBasic: false // Quick wins are premium
          });
        });
        
        // Add ROADMAP section if available
        if (roadmapText) {
          // Clean up any leftover formatting instructions
          const cleanRoadmap = roadmapText
            .replace(/\[|\]/g, '')
            .replace(/brief 3-4 sentence paragraph/i, '')
            .trim();
            
          insights.push({
            title: "AI Implementation Roadmap",
            description: cleanRoadmap,
            isBasic: false // Roadmap is premium
          });
        }
        
        // Ensure we have at least 2 basic insights for free tier
        if (insights.filter(i => i.isBasic).length < 2) {
          const industrySpecificInsight = generateIndustrySpecificInsight(auditData.industry, auditData.businessName);
          insights.unshift({
            title: industrySpecificInsight.title,
            description: industrySpecificInsight.description,
            isBasic: true
          });
          
          // If we still need another basic insight
          if (insights.filter(i => i.isBasic).length < 2) {
            insights.unshift({
              title: "Strategic AI Implementation Overview",
              description: `Our analysis has identified several opportunities for ${auditData.businessName} to leverage artificial intelligence to achieve your business goals, including ${auditData.businessGoals.join(', ')}.`,
              isBasic: true
            });
          }
        }
        
        // Format descriptions with impact included for display
        return insights.map(insight => ({
          ...insight,
          description: insight.impact 
            ? `${insight.description}\n\nBusiness Impact: ${insight.impact}`
            : insight.description
        }));
      } catch (e) {
        console.error('Error parsing AI insights:', e);
        // Fall back to generating static insights if parsing fails
        return generateFallbackInsights(auditData);
      }
    }
    
    // If no AI insights are available
    return generateFallbackInsights(auditData);
  };
  
  // Helper function to generate industry-specific fallback insights
  const generateIndustrySpecificInsight = (industry, businessName) => {
    switch(industry) {
      case 'retail':
        return {
          title: "Customer Experience Personalisation",
          description: `Retail businesses like ${businessName} can achieve up to 30% increase in customer satisfaction and 25% higher average order values with AI-driven personalisation systems.`
        };
      case 'finance':
        return {
          title: "AI-Powered Risk Assessment",
          description: `Financial institutions like ${businessName} using AI for risk assessment typically report 35% fewer false positives in fraud detection while improving regulatory compliance.`
        };
      case 'healthcare':
        return {
          title: "Patient Care Optimisation",
          description: `Healthcare providers implementing AI-powered scheduling and resource allocation systems report a 40% decrease in administrative overhead and significantly improved patient satisfaction.`
        };
      case 'manufacturing':
        return {
          title: "Predictive Maintenance Solutions",
          description: `Manufacturing operations like ${businessName} can reduce equipment downtime by up to 45% and maintenance costs by 30% through AI-powered predictive maintenance systems.`
        };
      default:
        return {
          title: "Process Automation Opportunity",
          description: `Businesses in your sector typically reduce operational costs by 30% and improve productivity by 25% through strategic AI implementation in core workflows.`
        };
    }
  };
  
  // Function to generate fallback insights if AI parsing fails
  const generateFallbackInsights = (data) => {
    const insights = [];
    const { industry, currentAIUse, businessGoals, businessName } = data;
    
    // Add industry-specific insight
    const industryInsight = generateIndustrySpecificInsight(industry, businessName);
    insights.push({
      ...industryInsight,
      isBasic: true
    });
    
    // Based on current AI usage
    if (currentAIUse === 'none') {
      insights.push({
        title: "AI Implementation Roadmap",
        description: "A detailed step-by-step plan for introducing AI capabilities into your existing operations with minimal disruption and maximum impact.",
        isBasic: false
      });
    } else if (currentAIUse === 'basic') {
      insights.push({
        title: "AI Integration Strategy",
        description: "Connect your existing AI tools into a cohesive ecosystem for maximum ROI and operational efficiency.",
        isBasic: false
      });
    } else if (currentAIUse === 'moderate') {
      insights.push({
        title: "AI Capability Expansion",
        description: "Advanced implementation strategies to build upon your existing AI foundation and drive further business value.",
        isBasic: false
      });
    }
    
    // Business goals insights
    if (businessGoals.includes('efficiency')) {
      insights.push({
        title: "Operational Efficiency Analysis",
        description: "Specific areas where AI can streamline your business processes based on industry benchmarks and your current workflows.",
        isBasic: businessGoals.length <= 2 // Make it free if they selected few goals
      });
    }
    
    if (businessGoals.includes('costs')) {
      insights.push({
        title: "Cost Reduction Opportunities",
        description: "Detailed breakdown of where AI can reduce operational expenses in your specific business context, with estimated ROI calculations.",
        isBasic: false
      });
    }
    
    if (businessGoals.includes('cx')) {
      insights.push({
        title: "Customer Experience Enhancement",
        description: "AI tools specifically designed to improve customer satisfaction and loyalty in your industry, with implementation guidance.",
        isBasic: businessGoals.length <= 2
      });
    }
    
    return insights;
  };
  
  // Get count of free vs premium insights
  const insights = getAIInsights();
  const freeInsightsCount = insights.filter(i => i.isBasic).length;
  const premiumInsightsCount = insights.filter(i => !i.isBasic).length;
  
  // SEO schema
  const auditSchema = {
    "@context": "https://schema.org",
    "@type": "Report",
    "name": "AI Business Audit Results",
    "creator": {
      "@type": "Organization",
      "name": "BetterByAI"
    },
    "description": "Personalised AI audit results revealing opportunities to transform your business operations."
  };
  
  // If no audit data was found, show error state
  if (!auditData) {
    return (
      <>
        <SEO 
          title="Audit Results | BetterByAI"
          description="View your personalised AI business audit results."
          canonical="https://betterbyai.com/audit-results"
        />
        
        <section className="pt-32 pb-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <ExclamationTriangleIcon className="w-16 h-16 text-[#FF7066] mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-primary mb-4">
                No Audit Data Found
              </h1>
              <p className="text-xl text-secondary mb-8">
                We couldn't find your audit information. Please complete the audit form to view your results.
              </p>
              <Link 
                to="/free-ai-audit" 
                className="inline-flex items-center bg-[#FF7066] text-white px-6 py-3 rounded-lg hover:bg-[#FF8A82] transition-colors"
              >
                Start Your Free AI Audit
                <ArrowRightIcon className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }
  
  return (
    <>
      <SEO 
        title="Your AI Audit Results | BetterByAI"
        description="Review your personalised AI business audit results and discover opportunities for business transformation."
        keywords="AI audit results, business transformation, AI opportunities, digital transformation"
        canonical="https://betterbyai.com/audit-results"
        schemaMarkup={auditSchema}
      />
    
      {/* Results Hero Section */}
      <section 
        ref={resultsRef}
        className="pt-32 pb-16 bg-gradient-to-b from-white via-[#FF7066]/5 to-white"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={resultsInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5 }}
              className="text-center mb-8"
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-[#FF7066]/10 to-secondary/10 text-[#FF7066] text-sm font-medium border border-[#FF7066]/10 mb-6">
                Your AI Audit Results
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
                Your <span className="bg-gradient-to-r from-[#FF7066] to-secondary bg-clip-text text-transparent">AI Opportunity</span> Analysis
              </h1>
              
              <p className="text-xl text-secondary">
                Based on your inputs, we've identified {freeInsightsCount + premiumInsightsCount} key AI opportunities for {auditData.businessName}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={resultsInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-100"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-2 bg-[#FF7066]/10 rounded-lg mr-3">
                    <ChartBarIcon className="w-6 h-6 text-[#FF7066]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-primary">Business Type</h3>
                    <p className="text-secondary capitalise">{auditData.industry.replace('-', ' ')}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-2 bg-[#FF7066]/10 rounded-lg mr-3">
                    <RocketLaunchIcon className="w-6 h-6 text-[#FF7066]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-primary">Current AI Usage</h3>
                    <p className="text-secondary capitalise">
                      {auditData.currentAIUse === 'none' ? 'Not yet implementing AI' : 
                       auditData.currentAIUse === 'basic' ? 'Basic AI implementation' : 
                       'Moderate AI implementation'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-2 bg-[#FF7066]/10 rounded-lg mr-3">
                    <LightBulbIcon className="w-6 h-6 text-[#FF7066]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-primary">Key Business Goals</h3>
                    <p className="text-secondary">
                      {auditData.businessGoals.map(goal => {
                        const labels = {
                          'efficiency': 'Operational Efficiency',
                          'costs': 'Cost Reduction',
                          'cx': 'Customer Experience',
                          'insights': 'Data Insights',
                          'innovation': 'Innovation',
                          'growth': 'Business Growth'
                        };
                        return labels[goal];
                      }).join(', ')}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-2 bg-[#FF7066]/10 rounded-lg mr-3">
                    <ClockIcon className="w-6 h-6 text-[#FF7066]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-primary">Implementation Timeline</h3>
                    <p className="text-secondary">
                      {auditData.implementationPriority === 'immediate' ? 'Immediate (1-3 months)' :
                       auditData.implementationPriority === 'long' ? 'Long-term (6+ months)' :
                       'Medium-term (3-6 months)'}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Free Insights Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={resultsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5 }}
                className="text-3xl font-bold text-primary mb-4"
              >
                Key AI Opportunities
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={resultsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-xl text-secondary"
              >
                Your free audit reveals {freeInsightsCount} insights, with {premiumInsightsCount} additional insights available in the full report.
              </motion.p>
            </div>
            
            <div className="space-y-8 mb-12">
              {insights.filter(insight => insight.isBasic).map((insight, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={resultsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.2 + (index * 0.1) }}
                  className="bg-white p-6 rounded-xl shadow-md border border-gray-100"
                >
                  <h3 className="text-xl font-bold mb-3 text-primary">
                    {insight.title}
                  </h3>
                  <p className="text-secondary">
                    {insight.description}
                  </p>
                  {insight.complexity && (
                    <div className="mt-3 flex items-center">
                      <span className="text-sm font-medium text-gray-700 mr-2">Implementation:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        insight.complexity.toLowerCase() === 'low' ? 'bg-green-100 text-green-800' : 
                        insight.complexity.toLowerCase() === 'medium' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-red-100 text-red-800'
                      }`}>
                        {insight.complexity}
                      </span>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
            
            {/* Locked Premium Insights Preview */}
            <div className="space-y-6 mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={resultsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="text-center"
              >
                <h3 className="text-2xl font-bold text-primary mb-6">
                  <LockClosedIcon className="inline-block w-6 h-6 mr-2 text-[#FF7066]" />
                  {premiumInsightsCount} Premium Insights Available
                </h3>
              </motion.div>
              
              {insights.filter(insight => !insight.isBasic).slice(0, 3).map((insight, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={resultsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.6 + (index * 0.1) }}
                  className="bg-gray-50 p-6 rounded-xl border border-gray-200 relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center">
                    <div className="p-3 bg-[#FF7066]/10 rounded-full">
                      <LockClosedIcon className="w-8 h-8 text-[#FF7066]" />
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-3 text-primary">
                    {insight.title}
                  </h3>
                  <p className="text-secondary">
                    {insight.description}
                  </p>
                </motion.div>
              ))}
              
              {insights.filter(insight => !insight.isBasic).length > 3 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={resultsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.9 }}
                  className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-center"
                >
                  <p className="text-lg text-primary font-medium">
                    + {insights.filter(insight => !insight.isBasic).length - 3} more premium insights...
                  </p>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>
      
      {/* Premium Report Upsell */}
      <section 
        ref={upsellRef} 
        className="py-16 bg-gradient-to-b from-white via-[#FF7066]/5 to-white"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={upsellInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-primary mb-4">
                  Unlock Your Complete AI Business Report
                </h2>
                <p className="text-xl text-secondary">
                  Get access to all {premiumInsightsCount} premium insights and a detailed implementation roadmap.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-12 mb-8">
                <div>
                  <h3 className="text-lg font-bold text-primary mb-4 flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-[#FF7066] mr-2" />
                    What You'll Get:
                  </h3>
                  <ul className="space-y-3">
                    {[
                      'Complete analysis of AI opportunities specific to your business',
                      'Detailed implementation roadmap with timeline',
                      'Cost-benefit analysis for each recommendation',
                      'Competitor AI adoption insights',
                      'Priority areas for maximum ROI',
                      'Technical requirements overview'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-[#FF7066] mr-2 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="md:border-l md:pl-8 border-gray-200">
                  <div className="text-center mb-6">
                    <p className="text-gray-500 line-through text-lg">Regular price: £199</p>
                    <div className="flex items-center justify-center">
                      <span className="text-4xl font-bold text-primary">£97</span>
                      <span className="ml-2 bg-[#FF7066]/10 text-[#FF7066] text-sm font-medium px-2 py-1 rounded-full">
                        50% OFF
                      </span>
                    </div>
                    
                    {/* Countdown timer */}
                    <div className="mt-4 mb-6">
                      <p className="text-sm text-gray-500 mb-2">Limited time offer expires in:</p>
                      <div className="flex items-center justify-center space-x-3">
                        <div className="bg-gray-100 px-3 py-2 rounded-lg">
                          <span className="text-xl font-bold text-primary">{formatNumber(timeLeft.hours)}</span>
                          <span className="text-xs block text-gray-500">Hours</span>
                        </div>
                        <span className="text-xl font-bold">:</span>
                        <div className="bg-gray-100 px-3 py-2 rounded-lg">
                          <span className="text-xl font-bold text-primary">{formatNumber(timeLeft.minutes)}</span>
                          <span className="text-xs block text-gray-500">Minutes</span>
                        </div>
                        <span className="text-xl font-bold">:</span>
                        <div className="bg-gray-100 px-3 py-2 rounded-lg">
                          <span className="text-xl font-bold text-primary">{formatNumber(timeLeft.seconds)}</span>
                          <span className="text-xs block text-gray-500">Seconds</span>
                        </div>
                      </div>
                    </div>
                    
                    <button 
                      className="w-full bg-[#FF7066] text-white px-6 py-4 rounded-lg hover:bg-[#FF8A82] transition-colors shadow-lg shadow-[#FF7066]/20 text-lg font-medium"
                      onClick={() => {
                        // Track conversion event if you have analytics
                        try {
                          if (typeof window !== 'undefined' && window.gtag) {
                            window.gtag('event', 'begin_checkout', {
                              currency: 'GBP',
                              value: 199.00,
                              items: [{
                                item_name: 'AI Business Audit Full Report',
                                price: 97.00
                              }]
                            });
                          }
                        } catch (e) {
                          console.error('Analytics error:', e);
                        }
                        
                        // Redirect to Stripe
                        window.open('https://buy.stripe.com/aEU4i39Lx2q78tWcMN', '_blank');
                      }}
                    >
                      Unlock Full Report Now
                    </button>
                    
                    <p className="mt-3 text-sm text-gray-500">
                      Secure payment • Instant delivery • 14-day guarantee
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Replace the testimonials section in the Premium Report Upsell */}
              <div className="border-t border-gray-100 pt-8 mt-8">
                <h3 className="text-center text-lg font-bold text-primary mb-6">
                  What's Inside Your AI Implementation Report
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                    <div className="flex items-start mb-3">
                      <div className="p-2 bg-[#FF7066]/10 rounded-lg mr-3">
                        <ChartBarIcon className="w-5 h-5 text-[#FF7066]" />
                      </div>
                      <h4 className="font-bold text-primary">ROI Analysis</h4>
                    </div>
                    <p className="text-gray-600">
                      Discover exactly how much time and money you can save with the right AI implementation. Includes projected ROI timeline and break-even analysis.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                    <div className="flex items-start mb-3">
                      <div className="p-2 bg-[#FF7066]/10 rounded-lg mr-3">
                        <RocketLaunchIcon className="w-5 h-5 text-[#FF7066]" />
                      </div>
                      <h4 className="font-bold text-primary">Implementation Roadmap</h4>
                    </div>
                    <p className="text-gray-600">
                      Step-by-step timeline with specific actions, technology requirements, and resource allocation to successfully deploy AI in your business.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                    <div className="flex items-start mb-3">
                      <div className="p-2 bg-[#FF7066]/10 rounded-lg mr-3">
                        <LightBulbIcon className="w-5 h-5 text-[#FF7066]" />
                      </div>
                      <h4 className="font-bold text-primary">Opportunity Prioritisation</h4>
                    </div>
                    <p className="text-gray-600">
                      Each AI opportunity ranked by implementation ease, cost, and potential impact so you know exactly where to start for maximum results.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                    <div className="flex items-start mb-3">
                      <div className="p-2 bg-[#FF7066]/10 rounded-lg mr-3">
                        <ClockIcon className="w-5 h-5 text-[#FF7066]" />
                      </div>
                      <h4 className="font-bold text-primary">Immediate Quick Wins</h4>
                    </div>
                    <p className="text-gray-600">
                      Specific AI tools and techniques you can implement within 30 days with minimal investment to demonstrate value quickly.
                    </p>
                  </div>
                </div>
                
                <div className="mt-8 p-4 bg-[#FF7066]/5 border border-[#FF7066]/20 rounded-lg text-center">
                  <p className="font-medium text-primary">
                    Get AI insights immediately plus a comprehensive human-reviewed PDF emailed to you within 48 hours, tailored specifically for {auditData.businessName}.
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Our experts review every report to provide strategic context, implementation guidance, and industry-specific recommendations. Includes 14-day satisfaction guarantee with email support.
                  </p>
                </div>
              </div>
            </motion.div>
            
            <div className="mt-6 bg-[#FF7066]/5 p-6 rounded-lg border border-[#FF7066]/10">
              <h3 className="text-lg font-bold text-primary mb-3">Why This Analysis is Valuable:</h3>
              <p className="text-secondary mb-4">
                Your business audit was generated by our advanced AI system that has analysed thousands of successful AI implementations across the {auditData.industry} industry.
              </p>
              <p className="text-secondary">
                Our complete report expands on these insights with detailed implementation steps, technical requirements, and ROI projections specific to your business needs.
              </p>
            </div>
            
            {/* Alternative CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={upsellInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-12 text-center"
            >
              <p className="text-secondary mb-6">
                Not ready to purchase yet? Book a free consultation to discuss your audit results.
              </p>
              <a 
                href="https://calendly.com/amrit-betterbyai/30min" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center border-2 border-[#FF7066] text-[#FF7066] px-6 py-3 rounded-lg hover:bg-[#FF7066]/5 transition-colors font-medium"
              >
                Schedule Free Consultation
                <ArrowRightIcon className="ml-2 w-5 h-5" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Expert Strategy Session */}
      <section className="py-16 bg-gradient-to-b from-white via-[#FF7066]/5 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={upsellInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-primary mb-4">
                  Want to Go Beyond AI?
                </h2>
                <p className="text-xl text-secondary">
                  Our AI analysis is just the start. Get expert human insights tailored to your specific situation.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-xl font-bold text-primary mb-6">
                    Book a Strategy Session with Our AI Implementation Experts
                  </h3>
                  <ul className="space-y-4">
                    {[
                      'Deep-dive into your specific business challenges',
                      'Get personalised advice from specialists in your industry',
                      'Learn implementation best practices from real-world case studies',
                      'Ask questions and get immediate, actionable answers',
                      'Receive a tailored implementation plan specific to your team and resources'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-[#FF7066] mr-2 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="mt-10">
                    <a 
                      href="https://calendly.com/amrit-betterbyai/30min" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center bg-gradient-to-r from-[#FF7066] to-secondary text-white px-8 py-4 rounded-lg hover:opacity-90 transition-opacity font-medium text-center"
                    >
                      Schedule Your Expert Session
                      <ArrowRightIcon className="ml-2 w-5 h-5" />
                    </a>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-xl font-bold text-primary mb-6">What to Expect in Your Session</h4>
                  
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 p-2 bg-[#FF7066]/10 rounded-lg mr-4">
                        <span className="block w-6 h-6 text-[#FF7066] font-bold text-center">1</span>
                      </div>
                      <div>
                        <h5 className="font-medium text-primary">Situation Analysis</h5>
                        <p className="text-secondary">We'll review your AI audit results and discuss your specific business challenges and goals.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 p-2 bg-[#FF7066]/10 rounded-lg mr-4">
                        <span className="block w-6 h-6 text-[#FF7066] font-bold text-center">2</span>
                      </div>
                      <div>
                        <h5 className="font-medium text-primary">Strategic Recommendations</h5>
                        <p className="text-secondary">Our experts will provide tailored advice based on your industry, resources, and priorities.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 p-2 bg-[#FF7066]/10 rounded-lg mr-4">
                        <span className="block w-6 h-6 text-[#FF7066] font-bold text-center">3</span>
                      </div>
                      <div>
                        <h5 className="font-medium text-primary">Implementation Planning</h5>
                        <p className="text-secondary">You'll receive a concrete next-steps plan with timeline and resource requirements.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 p-4 bg-gradient-to-r from-[#FF7066]/5 to-secondary/5 rounded-lg border border-[#FF7066]/10 flex items-center">
                    <LightBulbIcon className="w-8 h-8 text-[#FF7066] mr-4" />
                    <div>
                      <p className="font-medium text-primary">Expert-Led Session</p>
                      <p className="text-secondary text-sm">30-minute consultation with an industry specialist with 10+ years of AI implementation experience.</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}